export interface AgentProfile {
  id: string;
  email: string;
  phone?: string | null;
  name: string;
  status: string;
  costRate?: number;
  can_delegate_popups?: boolean;
  emailVerified?: boolean;
  phoneVerified?: boolean;
  emailVerifiedAt?: string | null;
  phoneVerifiedAt?: string | null;
}

export type AgentLoginSuccessData = {
  accessToken: string;
  refreshToken: string;
  agent?: Record<string, unknown>;
  superAgent?: Record<string, unknown>;
};

export type AgentLoginOtpChallengeData = {
  needsOtp: true;
  pendingToken: string;
  channel: 'email' | 'phone';
  maskedDestination?: string;
};

export interface AgentLoginResponse {
  success: boolean;
  data?: AgentLoginSuccessData | AgentLoginOtpChallengeData;
  message?: string;
}

export function isLoginOtpChallenge(
  data: AgentLoginResponse['data']
): data is AgentLoginOtpChallengeData {
  return !!data && 'needsOtp' in data && data.needsOtp === true;
}

export interface AgentDashboardData {
  credit_balance: number;
  /** USD online balance (UsdWallet.balanceNet) for balance → credits */
  online_balance_net?: number;
  active_shops_count: number;
  /** Super agent portal: agents under this super agent */
  managed_agents_count?: number;
  can_delegate_popups?: boolean;
  can_create_shop_popup?: boolean;
  can_manage_shop_popup_permission?: boolean;
  default_shop_can_create_player_popup?: boolean;
  low_balance_warning_enabled?: boolean;
  low_balance_threshold_usd?: number;
  low_balance_effective_threshold_usd?: number;
  /** Super agent cost rate 0–1; read-only in Settings. */
  cost_rate?: number;
  two_factor_enabled?: boolean;
  two_factor_channel?: 'email' | 'phone' | null;
  email_verified?: boolean;
  phone_verified?: boolean;
}

/** One row from GET /super-agent/transactions (wallet_transactions ledger). */
export interface AgentWalletLedgerCounterparty {
  ownerType: string;
  ownerId: string;
  /** Resolved on the server when listing ledger rows. */
  displayName?: string | null;
  roleLabel?: string | null;
}

export interface AgentWalletLedgerEntry {
  id: string;
  type: string;
  amount: number | null;
  balanceBefore: number | null;
  balanceAfter: number | null;
  description: string | null;
  status: string;
  direction: 'in' | 'out' | 'unknown';
  counterparty: AgentWalletLedgerCounterparty | null;
  fromWalletId: string | null;
  toWalletId: string | null;
  transactionId: string | null;
  referenceId: string | null;
  referenceType: string | null;
  costRate?: number | null;
  costAmount?: number | null;
  platformFeeRate?: number | null;
  platformFeeAmount?: number | null;
  createdAt?: string;
  /** Some API responses use snake_case; prefer `createdAt` when both exist. */
  created_at?: string;
}

export interface AgentTransactionsPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// ===== Shop USD wallet ledger (online balance) =====

export type ShopUsdWalletTxType =
  | 'deposit'
  | 'withdrawal'
  | 'purchase_credits'
  | 'adjustment'
  | 'reversal'
  | string;

export interface ShopUsdWalletLedgerParty {
  ownerType?: string;
  ownerId?: string | null;
  displayName?: string | null;
  roleLabel?: string;
  label?: string;
}

export interface ShopUsdWalletLedgerRow {
  id: string;
  usdWalletId: string;
  transactionId: string;
  type: ShopUsdWalletTxType;
  provider?: string | null;
  referenceType?: string | null;
  referenceId?: string | null;
  amountGross: string | number;
  amountNet: string | number;
  balanceGrossBefore: string | number;
  balanceGrossAfter: string | number;
  balanceNetBefore: string | number;
  balanceNetAfter: string | number;
  metadata?: Record<string, unknown> | null;
  status?: string;
  createdAt?: string;
  created_at?: string;
  usdWallet?: {
    id?: string;
    ownerType?: string;
    ownerId?: string;
    currency?: string;
  } | null;
  fromParty?: ShopUsdWalletLedgerParty | null;
  toParty?: ShopUsdWalletLedgerParty | null;
  ledgerType?: string | null;
  paymentMethod?: string | null;
  displayLabel?: string | null;
  displayCategory?: string | null;
}

export interface ShopUsdWalletLedgerResponse {
  ownerType: 'shop';
  ownerId: string;
  currency: string;
  walletId: string | null;
  balanceGross: string | number;
  balanceNet: string | number;
  status: string;
  count: number;
  rows: ShopUsdWalletLedgerRow[];
}

/** GET /super-agent/shops/:id/usd-wallet/ledger */
export interface ManagedShopUsdLedgerData {
  ownerType: string;
  ownerId: string;
  currency: string;
  walletId: string | null;
  balanceGross: number;
  balanceNet: number;
  status: string;
  count: number;
  rows: ShopUsdWalletLedgerRow[];
  pagination: AgentTransactionsPagination;
}

/** Response from POST /super-agent/credits/purchase (same shape as agent/shop CDN purchase). */
export interface AgentCreditPurchaseData {
  paymentUrl?: string | null;
  paymentAddress?: string | null;
  invoiceId?: string | null;
  transactionId?: string;
  amount: number;
  currency: string | null;
  creditAmount: number;
  endTime?: string | number | null;
  status: string;
  order?: Record<string, unknown>;
}

export interface ZeroxStaticWallet {
  id: string;
  ownerType: 'super_agent' | 'shop' | 'agent';
  ownerId: string;
  clientId: string;
  currency: string;
  address: string;
  tag?: string | null;
  enabled: boolean;
  raw?: unknown;
  created_at?: string;
  updated_at?: string;
}

export type AgentShopStatus = 'active' | 'suspended' | 'terminated';

export interface AgentShop {
  id: string;
  name: string;
  username?: string | null;
  email: string;
  phone: string | null;
  status: AgentShopStatus;
  costRate?: string | number;
  /** Agent cost baseline (0–1); shop cost must be strictly greater than this. */
  minShopCostRateExclusive?: number;
  createdAt?: string;
  updatedAt?: string;
  suspendedAt?: string | null;
  suspendedBy?: string | null;
  terminatedAt?: string | null;
  terminatedBy?: string | null;
  canCreatePlayerPopup?: boolean;
}

export interface AgentPlayer {
  id: string;
  username: string | null;
  email: string | null;
  phone: string | null;
  status: string;
  shop_id: string | null;
  shop_assigned_at: string | null;
  created_at?: string;
}

export interface SuperManagedAgent {
  id: string;
  name: string;
  username?: string | null;
  email: string;
  phone?: string | null;
  status: 'active' | 'suspended' | 'terminated';
  costRate?: number | string;
  /** CDN wallet balance (credits) for this agent, from GET /super-agent/agents */
  credit_balance?: number;
  can_create_shop_popup?: boolean;
  can_manage_shop_popup_permission?: boolean;
  default_shop_can_create_player_popup?: boolean;
  canCreateShopPopup?: boolean;
  canManageShopPopupPermission?: boolean;
  defaultShopCanCreatePlayerPopup?: boolean;
}

export interface SuperManagedShop {
  id: string;
  name: string;
  username?: string | null;
  email: string;
  phone?: string | null;
  status: 'active' | 'suspended' | 'terminated';
  costRate?: number | string;
  agent?: {
    id: string;
    name: string;
  };
}

/** GET /super-agent/shops/stats — date-range stats per shop across managed agents. */
export interface SuperAgentShopHierarchyStatsRange {
  from: string;
  to: string;
}

export interface SuperAgentShopHierarchyShopStat {
  id: string;
  name: string;
  status: string;
  agent_id: string;
  agent_name: string;
  total_users: number;
  new_users: number;
  total_deposit: number;
  total_withdrawal: number;
}

export interface SuperAgentShopHierarchyStatsData {
  range: SuperAgentShopHierarchyStatsRange;
  summary: {
    total_deposit: number;
    total_withdrawal: number;
    total_users: number;
    new_users: number;
  };
  shops: SuperAgentShopHierarchyShopStat[];
}

export interface SuperManagedPlayer {
  id: string;
  username: string | null;
  email: string | null;
  phone: string | null;
  status: string;
  shop_id: string | null;
  shop_name: string | null;
  agent_id: string | null;
  agent_name: string | null;
  shop_assigned_at?: string | null;
}
